import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { CurrencyType } from '../customer-billing-price/currency-type';
import { InvoiceStatusType, InvoiceType } from './invoice-type';
import { Injector } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { CustomerBillingInvoiceDetailModel } from './customer-billing-invoice-detail.model';
import { propComparator } from '@fi-shared/utils/array-sort-comparators';
import { fiPackageTypeName } from '../customer-billing-customer/package-type';
import { CustomerBillingCustomerModel } from '../customer-billing-customer/customer-billing-customer.model';
import { BillingContactModel } from '@fi-app/billing/billing-contact.model';
import { ICreateBase } from '@fi-shared/models/models';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

export interface ICustomerBillingInvoiceStatusData {
    Status: InvoiceStatusType;
    //PrintedDate: string; //ISO 8601 date
    //SentDate: string; //ISO 8601 date
    PaidDate: string; //ISO 8601 date
}

export interface ICustomerBillingInvoiceDate extends ICreateBase {}

export class CustomerBillingInvoiceModel {
    Id = undefined;
    InvoiceStatusType = undefined;
    CreatedOn = undefined;
    ChangedOn = undefined;
    InvoiceType = InvoiceType.Monthly;
    CustomerName = undefined;
    BillingCustomerId = undefined;
    CustomerReference = '';
    OurReference = '';
    InvoiceDate = undefined;
    DueDate = undefined;
    PaidDate = undefined;

    //SentDate = undefined;
    SentDates: ICustomerBillingInvoiceDate[] = [];

    //PrintedDate = undefined;
    PrintedDates: ICustomerBillingInvoiceDate[] = [];

    TotalAmountExcVat = 0.0;
    TotalAmountIncVat = 0.0;
    CurrencyType = CurrencyType.SEK;
    CustomerVATNumber = '';
    ChargeVAT = true;
    Address = undefined;
    InvoiceDetails: CustomerBillingInvoiceDetailModel[] = [];

    OurReferencesListData = new DropdownSettingsModelV2<BillingContactModel>({
        dataValueField: 'Name',
    });

    CustomerReferencesListData = new DropdownSettingsModelV2<BillingContactModel>({
        dataValueField: 'Name',
    });

    InvoiceDateFormatOptions: Intl.DateTimeFormatOptions = {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    };

    constructor(data) {
        if (data) this.Initialize(data);
    }

    Initialize(invoice) {
        Object.assign(this, invoice);
        this.InvoiceDetails = this.InvoiceDetails.map((itemData) => {
            return new CustomerBillingInvoiceDetailModel(itemData);
        });
    }

    clearPriceArticles() {
        this.InvoiceDetails = [];
    }

    setupInitialMonthlyArticles(articles, appTextService: AppTextService, fiFormatDateService: FiFormatDateService) {
        this.clearPriceArticles();

        const invoiceMonth = CustomerBillingInvoiceModel.LastMonth();

        const paymentPeriodComment = new CustomerBillingInvoiceDetailModel({
            Description: String.format(
                'Invoice period:\r\n{0} - {1}',
                fiFormatDateService.formatDate(invoiceMonth.Start, this.InvoiceDateFormatOptions),
                fiFormatDateService.formatDate(invoiceMonth.End, this.InvoiceDateFormatOptions),
            ),
            InvoiceDetailType: CustomerBillingInvoiceDetailModel.InvoiceDetailTypeComment,
        });

        this.InvoiceDetails.push(paymentPeriodComment);

        articles.forEach((article) => {
            if (article.IsModuleCharge)
                article.Description = String.format(
                    '{0} - {1}',
                    article.Description,
                    fiPackageTypeName(article.PackageType, appTextService),
                );

            this.InvoiceDetails.push(article);
        });
    }

    setOwnerInformation(owner: CustomerBillingCustomerModel) {
        const billingReferences = owner.InvoiceInfo.BillingReference.sort(propComparator('IsDefault'));
        if (!this.OurReference) this.OurReference = (billingReferences[0] || new BillingContactModel()).Name;

        this.OurReferencesListData.setDataSet(billingReferences);
    }

    setCustomerReferences(customer, selectedReference?) {
        const billingReferences = customer.InvoiceInfo.BillingReference.sort(propComparator('IsDefault'));

        if (selectedReference && !billingReferences.filter((item) => item.Name == selectedReference).length)
            billingReferences.unshift({ Name: selectedReference });

        this.CustomerReferencesListData.setDataSet(billingReferences);
    }

    setCustomerInformation(customer) {
        this.setCustomerReferences(customer);

        if (this.CustomerReferencesListData.dataSet && this.CustomerReferencesListData.dataSet.length > 0)
            this.CustomerReference = this.CustomerReferencesListData.dataSet[0].Name;

        this.BillingCustomerId = customer.BillingCustomerId;
        this.CustomerName = customer.Name;
        this.CustomerVATNumber = customer.VATNumber
            ? String.format('{0}{1}', customer.VATNumber.CountryCode, customer.VATNumber.Number)
            : '';
        this.ChargeVAT = customer.ChargeVAT;
        this.Address = customer.Address;
    }

    clearCustomerInformation() {
        this.CustomerReference = undefined;
        this.CustomerReferencesListData.setDataSet([]);
        this.BillingCustomerId = undefined;
        this.CustomerName = undefined;
        this.CustomerVATNumber = undefined;
        this.Address = undefined;
    }

    getLogoSrc() {
        return window.location.origin + '/Content/Images/logo.jpg';
    }

    toJSON() {
        let result: { [prop: string]: any } = {};
        for (let x in this) {
            if (x !== 'OurReferencesListData' && x !== 'CustomerReferencesListData') result[x] = this[x];
        }
        return result;
    }

    static LastMonth() {
        const currentMonthStart: any = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        const lastMonthEnd = new Date(currentMonthStart - 1);

        return {
            Start: new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1),
            End: lastMonthEnd,
        };
    }
    static StatusTypePaid = 3;
}
